import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useAuth } from '@/hooks/useAuth';
import { useCreateActivity } from '@/hooks/activities/useActivities';
import { useFeed } from '@/hooks/useFeed';
import type { CoinCategory, CoinCity, CoinPost, Activity } from '@/lib/types';
import { createCoinPost } from '@/services/coinService';
import { ActivityTypeNum, HelpTypeNum, CoinPostCategoryNum, type SelectedPlace } from '@/lib/types';
import { cityLabel } from '@/pages/le-coin/coinMeta';
import FilterBar, { type TimeFilter, type TypeFilter } from './components/FilterBar';
import ActivityCard from './components/ActivityCard';
import CoinFeedCard from './components/CoinFeedCard';
import MapPanel from './components/MapPanel';
import PublishModal from './components/PublishModal';
import LoadMore from './components/LoadMore';
import bannerYonYon from '@/assets/bannerAuth.jpg'
import { useMapActivities } from '@/hooks/activities/useMapActivities';
import { Helmet } from "react-helmet-async";

type FeedItem =
  | { kind: 'activity'; activity: Activity }
  | { kind: 'coin'; post: CoinPost };


export default function Home() {
  const { user, joinedIds, toggleJoin } = useAuth();
  const navigate = useNavigate();

  const [selectedType, setSelectedType] = useState<TypeFilter>('ALL');
  const [selectedDistance, setSelectedDistance] = useState<number>(60);
  const [selectedTime, setSelectedTime] = useState<TimeFilter>('UPCOMING');
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [removedCoinPostIds, setRemovedCoinPostIds] = useState<string[]>([]);


  // --- Feed desde API ---
  const { activities: apiActivities, coinPosts: apiCoinPosts, loading, loadingMore, totalItems, error, refresh, displayedCount, hasMore, } = useFeed(
    selectedType,
    selectedDistance,
    selectedTime,
    user?.id,
    currentPage
  );
  const createActivityMutation = useCreateActivity();

  const { data: mapActivities = [], isLoading: mapLoading } = useMapActivities();

  const showToast = (msg: string) => {
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    setToast(msg);
    toastTimer.current = window.setTimeout(() => setToast(null), 3200);
  };

  // Filtrage local sur search
  const filteredActivities = useMemo(() => {
    if (!search.trim()) return apiActivities;
    const q = search.trim().toLowerCase();
    return apiActivities.filter((a) => {
      const hay = `${a.title} ${a.description} ${a.location} ${a.tags.join(' ')}`.toLowerCase();
      return hay.includes(q);
    });
  }, [apiActivities, search]);

  const filteredPosts = useMemo(() => {
    const visiblePosts = apiCoinPosts.filter(
      (p) => !removedCoinPostIds.includes(p.id)
    );

    if (!search.trim()) return visiblePosts;

    const q = search.trim().toLowerCase();

    return visiblePosts.filter((p) => {
      const hay = `${p.content} ${cityLabel(p.city)} ${p.authorAlias}`.toLowerCase();
      return hay.includes(q);
    });
  }, [apiCoinPosts, search, removedCoinPostIds]);


  const feedItems = useMemo<FeedItem[]>(() => {
    const activityItems: FeedItem[] = filteredActivities.map((activity) => ({
      kind: 'activity',
      activity,
    }));

    if (selectedType === 'COIN') {
      return filteredPosts.map((post) => ({
        kind: 'coin',
        post,
      }));
    }

    if (selectedType === 'ALL') {
      const mixedItems: FeedItem[] = [
        ...activityItems,
        ...filteredPosts.map(
          (post): FeedItem => ({
            kind: 'coin',
            post,
          })
        ),
      ];

      return mixedItems.sort((a, b) => {
        const dateA =
          a.kind === 'activity'
            ? a.activity.createdAt
            : a.post.createdAt;

        const dateB =
          b.kind === 'activity'
            ? b.activity.createdAt
            : b.post.createdAt;

        return new Date(dateB).getTime() - new Date(dateA).getTime();
      });
    }

    return activityItems;
  }, [filteredActivities, filteredPosts, selectedType]);


  const handleCreateClick = () => {
    if (!user) {
      showToast('Connecte-toi pour publier une activité');
      navigate('/auth');
      return;
    }

    setModalOpen(true);
  }

  const handleJoin = (id: string) => {
    if (!user) {
      showToast('Connecte-toi pour rejoindre une activité');
      navigate('/auth');
      return;
    }
    toggleJoin(id);
  }


  // Fonction de création d'une activité
  const handlePublishActivity = async (data: { type: Activity['type']; title: string; description: string; place: SelectedPlace; startsAt?: string; endsAt?: string; maxParticipants?: number; tags: string[] }) => {
    if (!user) {
      showToast('Connecte-toi pour publier une activité');
      navigate('/auth');
      return;
    }

    const typeMap: Record<Activity['type'], ActivityTypeNum> = {
      SOCIAL: ActivityTypeNum.Social,
      SPORT: ActivityTypeNum.Sport,
      HELP_REQUEST: ActivityTypeNum.Help,
      HELP_OFFER: ActivityTypeNum.Help,
    };

    const helpTypeMap: Record<Activity['type'], HelpTypeNum | undefined> = {
      SOCIAL: undefined,
      SPORT: undefined,
      HELP_REQUEST: HelpTypeNum.Request,
      HELP_OFFER: HelpTypeNum.Offer,
    };

    try {

      // Google Palces suggestions Selection
      await createActivityMutation.mutateAsync({
        type: typeMap[data.type],
        helpType: helpTypeMap[data.type],
        title: data.title,
        description: data.description || undefined,

        publicLocationLabel: data.place.label,
        latitude: data.place.latitude,
        longitude: data.place.longitude,

        startsAt: data.startsAt,
        endsAt: data.endsAt,
        maxParticipants: data.maxParticipants,
        tags: data.tags,
      });

      setCurrentPage(1);
      await refresh();

      setModalOpen(false);
      showToast('Activité publiée !');
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message
          : 'Erreur lors de la publication';

      showToast(msg);
    }
  };

  // HandlePublishCoin
  const handlePublishCoin = async (data: { category: CoinCategory; city: CoinCity; content: string; }) => {
    if (!user) {
      showToast('Connecte-toi pour publier sur Le Coin');
      navigate('/auth');
      return;
    }

    const catMap: Record<CoinCategory, CoinPostCategoryNum> = {
      VU: CoinPostCategoryNum.Vu,
      CONFESSION: CoinPostCategoryNum.Confession,
      COUP_DE_COEUR: CoinPostCategoryNum.CoupDeCoeur,
      QUESTION: CoinPostCategoryNum.Question,
    };

    try {
      await createCoinPost({
        category: catMap[data.category],
        content: data.content,
        isAnonymous: true,
        areaLabel: data.city,
      });
      setModalOpen(false);
      showToast('Publié anonymement sur Le Coin !');
      refresh();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erreur lors de la publication';
      showToast(msg);
    }
  };

  // Methode de chargement des activités
  const handleLoadMore = () => {
    if (!hasMore || loading) return;

    setCurrentPage((prev) => prev + 1);
  };


  const handleTypeChange = (value: TypeFilter) => {
    setCurrentPage(1);
    setSelectedType(value);
  };

  const handleDistanceChange = (value: number) => {
    setCurrentPage(1);
    setSelectedDistance(value);
  };

  const handleTimeChange = (value: TimeFilter) => {
    setCurrentPage(1);
    setSelectedTime(value);
  };


  // Retire immédiatement du feed un post Le Coin après une modération réussie.
  const handleCoinPostRemoved = (postId: string) => {
    setRemovedCoinPostIds((prev) => [...prev, postId]);
  };


  return (

    <>
      {/* Balise SEO */}
      <Helmet>
        <title>YonYon — Activités, événements & entraide à Dole</title>
        <meta
          name="description"
          content="YonYon, le réseau de proximité du Grand Dole pour découvrir des activités, participer à des événements et s'entraider entre habitants."
        />
        <link rel="canonical" href="https://yonyon.fr/" />
        <meta property="og:title" content="YonYon — Activités, événements & entraide à Dole" />
        <meta property="og:description" content="Découvre des activités, des événements et des initiatives d'entraide près de chez toi dans le Grand Dole." />
        <meta property="og:url" content="https://yonyon.fr/" />
        <meta property="og:image" content="https://yonyon.fr/yonyon-partage.png" />
        <meta name="twitter:title" content="YonYon — Activités, événements & entraide à Dole" />
        <meta name="twitter:description" content="Découvre des activités, des événements et des initiatives d'entraide près de chez toi dans le Grand Dole." />
        <meta name="twitter:image" content="https://yonyon.fr/yonyon-partage.png" />
      </Helmet>

      <div className="min-h-screen flex flex-col bg-background-50">
        <Navbar
          onCreateClick={handleCreateClick}
          search={search}
          onSearchChange={setSearch}
        />

        {/* Hero */}
        <section
          id="accueil"
          className="
            relative isolate w-full overflow-hidden 
            h-[250px] 
            min-[926px]:h-[400px]
            [@media(min-width:926px)_and_(max-height:800px)]:h-[280px]
           [@media(min-width:926px)_and_(min-height:801px)_and_(max-height:999px)]:h-[360px]
          "
        >
          {/* Image */}
          <div className="absolute inset-0 overflow-hidden">
            <img
              src={bannerYonYon}
              alt="Vue chaleureuse de Dole au coucher du soleil"
              className="
              w-full
              h-[calc(100%+40px)]
              object-cover
              object-[77%_center]
              -translate-y-[20px]

              min-[926px]:h-full
              min-[926px]:translate-y-0
              min-[926px]:object-center

              max-[430px]:object-[79%_center]
            "
            />

            <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/45 to-foreground-950/10" />
          </div>

          {/* Contenu */}
          <div
            className="
               absolute z-10
              left-0 bottom-5
              w-full
              px-4

              min-[926px]:bottom-8
              min-[926px]:px-8

              [@media(min-width:926px)_and_(max-height:800px)]:bottom-4
              [@media(min-width:926px)_and_(max-height:800px)]:px-6

              [@media(min-width:926px)_and_(min-height:801px)_and_(max-height:999px)]:bottom-6
            "
          >
            <div className="w-full max-w-7xl mx-auto max-[430px]:translate-y-[10px]">
              <div className="max-w-xl max-[925px]:max-w-lg">

                {/* Localisation */}
                <span
                  className="
                inline-flex items-center gap-2
                px-3 py-1.5
                rounded-full
                bg-white/15
                text-white text-[.8rem]
                font-medium backdrop-blur

                max-[925px]:px-2.5
                max-[925px]:py-1
                max-[925px]:text-x
                max-[390px]:text-[0.75rem]
              "
                >
                  <i className="ri-map-pin-2-line" />
                  Grand Dole · autour de toi
                </span>

                {/* Titre */}
                <h1
                  className="
                  mt-4
                  font-['Plus_Jakarta_Sans']
                  text-5xl
                  font-semibold
                  text-white
                  leading-tight

                  max-[925px]:mt-3
                  max-[925px]:text-3xl

                  max-[640px]:text-[1.75rem]
                  max-[430px]:text-[1.70rem]
                  max-[430px]:leading-[1.15]
                  max-[390px]:leading-[1.2]

                  [@media(min-width:926px)_and_(max-height:800px)]:mt-2
                  [@media(min-width:926px)_and_(max-height:800px)]:text-3xl

                  [@media(min-width:926px)_and_(min-height:801px)_and_(max-height:999px)]:text-[3.25rem]
                  [@media(min-width:926px)_and_(min-height:801px)_and_(max-height:999px)]:leading-[1.1]
                  [@media(min-width:926px)_and_(min-height:801px)_and_(max-height:999px)]:mt-3
                "
                >
                  Envie de faire un truc à <br /> Dole&nbsp;?
                </h1>

                {/* Description */}
                <p
                  className="
                  mt-3
                  text-lg
                  font-medium
                  text-white/85
                  leading-relaxed

                  max-[925px]:mt-2
                  max-[925px]:text-sm
                  max-[925px]:leading-snug

                  max-[412px]:text-[0.85rem]
                  max-[412px]:font-semibold

                  max-[390px]:text-x
                  max-[390px]:leading-[1.35]

                  max-[375px]:text-[0.8rem]

                  [@media(min-width:926px)_and_(max-height:800px)]:mt-1
                  [@media(min-width:926px)_and_(max-height:800px)]:text-sm
                  [@media(min-width:926px)_and_(max-height:800px)]:leading-snug

                  [@media(min-width:926px)_and_(min-height:801px)_and_(max-height:999px)]:mt-2
                  [@media(min-width:926px)_and_(min-height:801px)_and_(max-height:999px)]:text-base
                "
                >
                  Découvre ce qui se passe autour de toi, partage avec les gens du coin,
                  propose une activité ou rejoins celles des autres.
                </p>
                {/* Recherche */}
                <div
                  className="
                  mt-6
                  flex items-center gap-2
                  bg-white
                  rounded-md
                  p-1.5
                  border border-white/20

                  max-[925px]:mt-4
                  max-[925px]:p-1
                  max-[925px]:hidden

                  [@media(min-width:926px)_and_(max-height:800px)]:mt-3
                  [@media(min-width:926px)_and_(max-height:800px)]:p-1

                  [@media(min-width:926px)_and_(min-height:801px)_and_(max-height:999px)]:mt-4
                  [@media(min-width:926px)_and_(min-height:801px)_and_(max-height:999px)]:p-1.5
                "
                >
                  <span className="w-8 h-8 shrink-0 flex items-center justify-center text-foreground-400">
                    <i className="ri-search-line text-lg" />
                  </span>

                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Rechercher une sortie, une activité, un evènement..."
                    className="
                    flex-1 min-w-0
                    text-sm
                    text-foreground-900
                    placeholder:text-foreground-300
                    focus:outline-none

                    max-[925px]:text-xs
                  "
                  />

                  <button
                    type="button"
                    className="
                    px-4 py-2
                    rounded-md
                    bg-primary-500
                    text-white
                    text-sm font-semibold
                    hover:bg-primary-600
                    transition-colors
                    cursor-pointer
                    whitespace-nowrap

                    max-[925px]:px-3
                    max-[925px]:py-1.5
                    max-[925px]:text-[.9rem]

                    [@media(min-width:926px)_and_(max-height:800px)]:px-3
                    [@media(min-width:926px)_and_(max-height:800px)]:py-1.5
                "
                  >
                    Rechercher
                  </button>
                </div>

              </div>
            </div>
          </div>

          {/* Signature YonYon */}
          <div
            className="
            absolute z-10
            flex flex-col items-center
            text-center
            pointer-events-none
            rotate-[-3deg]

            top-5 right-4
            max-w-[145px]

            min-[344px]:top-[1rem]
            min-[430px]:right-5
            min-[430px]:max-w-[165px]

            min-[641px]:top-7
            min-[641px]:right-7
            min-[641px]:max-w-[190px]

            min-[926px]:top-[11%]
            min-[926px]:right-[4%]
            min-[926px]:-translate-y-1/2
            min-[926px]:max-w-[220px]

            min-[1100px]:right-[6%]
            min-[1100px]:max-w-[260px]  
            
            [@media(min-width:926px)_and_(max-height:800px)]:top-4
            [@media(min-width:926px)_and_(max-height:800px)]:right-5
            [@media(min-width:926px)_and_(max-height:800px)]:translate-y-0
            [@media(min-width:926px)_and_(max-height:800px)]:max-w-[180px]
          "
            style={{ transform: "rotate(-3deg)" }}
          >
            <p
              className="
              font-['Plus_Jakarta_Sans']
              italic
              font-medium
              leading-[1.18]
              text-white
              drop-shadow-sm

              text-[0.85rem]
               max-[344px]:text-[.78rem]
              max-[412px]:text-[.9rem]
              max-[641px]:rotate-[-4deg]

              min-[641px]:text-[1.1rem]
              min-[926px]:text-[1.35rem]
              min-[1100px]:text-[1.5rem] 
              [@media(min-width:926px)_and_(max-height:800px)]:text-[1.05rem]
              [@media(min-width:926px)_and_(min-height:801px)_and_(max-height:999px)]:text-[1.2rem]           
            "
            >
              Une application qui te reconnecte
              <br />
              avec les gens autour de toi
            </p>

            <svg
              viewBox="0 0 160 28"
              className="
              mt-[0.2rem]
              w-16
              h-auto
              text-primary-500

              min-[430px]:w-20
              min-[641px]:w-24
              min-[926px]:mt-2
              min-[926px]:w-28
              min-[1100px]:mt-3
              min-[1100px]:w-32

              [@media(min-width:926px)_and_(max-height:800px)]:mt-1
              [@media(min-width:926px)_and_(max-height:800px)]:w-20

              [@media(min-width:926px)_and_(min-height:801px)_and_(max-height:999px)]:w-24
            "
              aria-hidden="true"
            >
              <path
                d="M5 20 C45 29, 105 22, 155 5"
                fill="none"
                stroke="currentColor"
                strokeWidth="4"
                strokeLinecap="round"
              />
            </svg>
          </div>
        </section>

        {/* Main content */}
        <main className="flex-1 w-full px-4 py-8 md:px-8 md:py-10 max-[430px]:px-4 max-[430px]:py-[1.4rem]">
          <div className="max-w-7xl mx-auto">
            <FilterBar
              selectedType={selectedType}
              onTypeChange={handleTypeChange}
              selectedDistance={selectedDistance}
              onDistanceChange={handleDistanceChange}
              selectedTime={selectedTime}
              onTimeChange={handleTimeChange}
              resultCount={totalItems}
            />

            {/* Mobile : compteur + action Publier */}
            <div className="md:hidden flex items-center justify-between gap-3 mt-6">
              <span className="text-[0.875rem] leading-[1.2rem] text-foreground-500 min-w-0">
                <strong className="text-foreground-900 font-semibold">
                  {totalItems}
                </strong>{' '}
                {totalItems > 1 ? 'publications' : 'publication'} autour de toi
              </span>

              <button
                type="button"
                onClick={handleCreateClick}
                className="shrink-0 inline-flex items-center gap-1.5 px-3 py-[0.3rem] leading-[1.2rem] rounded-full bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer"
              >
                <i className="ri-add-line text-base" />
                Publier
              </button>
            </div>

            <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Feed */}
              <section id="activites" className="lg:col-span-2">
                {loading ? (
                  <div className="flex flex-col gap-4">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="bg-white border border-background-200 rounded-lg p-5 animate-pulse">
                        <div className="flex items-start gap-3">
                          <div className="w-12 h-12 rounded-lg bg-background-100 shrink-0"></div>
                          <div className="flex-1">
                            <div className="h-3 bg-background-100 rounded w-24 mb-2"></div>
                            <div className="h-5 bg-background-100 rounded w-3/4 mb-2"></div>
                            <div className="h-3 bg-background-100 rounded w-full"></div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : error ? (
                  <div className="bg-white border border-background-200 rounded-lg p-10 text-center">
                    <div className="w-14 h-14 mx-auto rounded-full bg-background-100 flex items-center justify-center text-foreground-400">
                      <i className="ri-wifi-off-line text-2xl"></i>
                    </div>
                    <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-950">
                      Impossible de charger le feed
                    </h3>
                    <p className="mt-1 text-sm text-foreground-500">{error}</p>
                    <button
                      type="button"
                      onClick={refresh}
                      className="mt-4 px-4 py-2 rounded-md bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Réessayer
                    </button>
                  </div>
                ) : feedItems.length === 0 ? (
                  <div className="bg-white border border-background-200 rounded-lg p-10 text-center">
                    <div className="w-14 h-14 mx-auto rounded-full bg-background-100 flex items-center justify-center text-foreground-400">
                      <i className="ri-search-eye-line text-2xl"></i>
                    </div>
                    <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-950">
                      Rien ici pour l'instant
                    </h3>
                    <p className="mt-1 text-sm text-foreground-500">
                      Élargis ton rayon ou lance la première publication de ton quartier.
                    </p>
                    <button
                      type="button"
                      onClick={handleCreateClick}
                      className="mt-4 px-4 py-2 rounded-md bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Publier
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="flex flex-col gap-[.8rem] md:gap-4">
                      {feedItems.map((item, index) => (
                        <div
                          key={item.kind === 'activity' ? item.activity.id : item.post.id}
                          className="animate-fade-in-up"
                          style={{ animationDelay: `${index * 60}ms` }}
                        >
                          {item.kind === 'activity' ? (
                            <ActivityCard
                              activity={item.activity}
                              isJoined={joinedIds.includes(item.activity.id)}
                              onJoin={handleJoin}
                              onNotify={showToast}
                            />
                          ) : (
                            <CoinFeedCard
                              post={item.post}
                              onCreateSortie={() => setModalOpen(true)}
                              onNotify={showToast}
                              onRemoved={handleCoinPostRemoved}
                            />
                          )}
                        </div>
                      ))}
                    </div>

                    <LoadMore
                      displayedCount={displayedCount}
                      totalCount={totalItems}
                      loading={loadingMore}
                      hasMore={hasMore}
                      onLoadMore={handleLoadMore}
                    />
                  </>
                )}
              </section>

              {/* Map / side */}
              <aside id="carte" className="lg:col-span-1 scroll-mt-20">
                <div className="lg:sticky lg:top-24">
                  <MapPanel
                    onCreateClick={handleCreateClick}
                    activities={mapActivities}
                  />
                </div>
              </aside>
            </div>
          </div>
        </main>

        <Footer />

        {/* Modal */}
        {modalOpen && (
          <PublishModal
            onClose={() => setModalOpen(false)}
            onPublishActivity={handlePublishActivity}
            onPublishCoin={handlePublishCoin}
          />
        )}

        {/* Toast */}
        {toast && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-scale-in">
            <div className="flex items-center gap-2.5 px-4 py-3 rounded-lg bg-foreground-900 text-white text-sm font-medium">
              <i className="ri-checkbox-circle-line text-accent-400 text-lg"></i>
              {toast}
            </div>
          </div>
        )}
      </div>
    </>
  );
}